import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import _isEmpty from 'lodash-es/isEmpty';
import _get from 'lodash-es/get';
import Layout from 'sushiweb/Layout/Layout';
import Button from 'sushiweb/Button/Button';
import MenuItem from 'sushiweb/MenuItem/MenuItem';
import Dropdown from 'sushiweb/Dropdown/Dropdown';
import Carousel from 'sushiweb/Carousel';

import H5 from 'sushiweb/Typography/Heading/H5';
import H6 from 'sushiweb/Typography/Heading/H6';
import H2 from 'sushiweb/Typography/Heading/H2';
import Tooltip from 'sushiweb/Tooltip/Tooltip';
import ChevronRight from 'sushiweb/Icons/all/ChevronRight';
import red from 'sushiweb/tokens/color/red';
import grey from 'sushiweb/tokens/color/grey';
import { P } from 'sushiweb/Typography/Text';
import {
  BottomScroll,
  maxStartDate,
  minStartDate,
} from '../../common/helpers/inputHandlers';
import { FormCacheContext } from '../../common/Components/FormTemplate/FormTemplate';
import { programStartDateError } from '../../../../constants';
import {
  ErrorContainer,
  InputWrapper,
  LabelWrapper,
  StyledInput,
  StyledLayout,
  StyledTextArea,
} from '../../../../styles';
import { genericErrorMsg } from '../../../../../../constants';
import {
  adhocProgramFormStates,
  currentFlowTypeEnum,
  cycleTypeEnum,
  programRestrictionsTypes,
  programStatusEnum,
  topupTypeStatusEnum,
} from '../../../../../common/components/constants';
import { validateEmail } from '../../../../../../utility';
import Card from '../../../../../common/components/Card';
import {
  FundsSummaryText,
  UploadFileWrapper,
} from '../AddEmployeeToProgram/styles';
import { getAllGiftCards } from '../../../../../../api/giftCard';
import { openErrorToast } from '../../../../../../reducers/uiLogic';
import ZomatoGiftCard from '../../../../../../constants/giftCardPreview';
import {
  formatAmountInRupees,
  isConnectedToStagging,
} from '../../../../../../helpers';
import { getOffices } from '../../../../../../api/companyProfile';
import ZTrackingClient from '../../../../../../jumbo/ZTrackingClient';
import CreateProgramModalContext from '../../../CreateProgramModalContext';

const AdhocProgram = ({
  subTitle,
  userData,
  companyProfile,
  flowType,
  planData,
  planDataFlag,
  selectedWorkspace,
  showErrorToast,
  reviewProgram,
  reviewingProgram,
  reviewProgramDetails,
  fetchingProgramInfo,
  fetchedProgramInfo,
  programInfo,
  getProgramInfo,
}) => {
  const {
    bodyStyle,
    footerStyle,
    stepsVisibility,
    goToNextStep,
    getCachedFormData,
    updateCachedFormData,
  } = useContext(FormCacheContext);
  const { setModalSubtitle } = useContext(CreateProgramModalContext);
  const [nextPlagFlag, setNextPlagFlag] = useState(false);
  const [errorValidation, setErrorValidation] = useState([]);
  const [disabled, setDisabled] = useState(true);
  const [GSTList, setGSTList] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState({});
  const [gcTypes, setGcTypes] = useState([]);
  const [selectedGcType, setSelectedGcType] = useState({});
  const [GcCardList, setGcCardList] = useState([]);
  const [selectedGcCardList, setSelectedGcCardList] = useState([]);
  const [selectedGcCard, setSelectedGcCard] = useState({});
  const [msgError, setMsgError] = useState('');

  const [formData, setFormData] = useState({
    programId: planData?.program_id ?? '',
    name: planData?.name ?? userData?.name,
    email: planData?.poc_email ?? userData?.email,
    workspaceID: selectedWorkspace?.workspace_id,
    amount: planData?.amount,
    startDate: planData?.start_date ?? minStartDate,
    gcTypeId: planData?.gift_card_details?.type_id ?? selectedGcCard?.type_id,
    gcCardId: planData?.gift_card_details?.card_id ?? selectedGcCard?.card_id,
    message: planData?.gift_card_details?.message ?? selectedGcCard?.message,
    gcAmount: planData?.gift_card_details?.amount ?? 0,
    showError: false,
  });
  const updateFormData = newData => {
    setFormData(oldData => ({
      ...oldData,
      ...newData,
    }));
  };

  useEffect(() => {
    getOffices()
      .then(res => {
        if (!res.status) {
          const {
            data: { message = {} },
          } = res;
          const errorMessage = _get(message, 'title', genericErrorMsg);
          openErrorToast(errorMessage);
          return;
        }
        setGSTList(res.data ?? []);
        if (res.data.length > 0) {
          setSelectedOffice(res.data[0]);
        }
      })
      .catch(error => {
        openErrorToast('Failed to fetch office details');
        console.error(error);
      });
  }, []);

  useEffect(() => {
    ZTrackingClient.recordImpression({
      ui_source: 'SEND_EGIFT_CARD_MODAL',
      component: 'MODAL',
    });
    getAllGiftCards({ user_id: userData?.user_id, greeting_type: '' })
      .then(res => {
        if (!res.status) {
          const {
            data: { message = {} },
          } = res;
          const errorMessage = _get(message, 'title', genericErrorMsg);
          openErrorToast(errorMessage);
          return;
        }

        const { types_data, gift_card_details } = res;

        setGcTypes(types_data);

        setGcCardList(gift_card_details);

        if (types_data.length > 0) setSelectedGcType(types_data[0]);
        updateFormData({ gcTypeId: types_data[0].type_id });
      })
      .catch(error => {
        openErrorToast('Failed to fetch gift cards');
        console.error(error);
      });
  }, []);

  useEffect(() => {
    if (selectedGcType && GcCardList.length > 0) {
      setSelectedGcCardList(
        GcCardList.filter(value => value.type_id === selectedGcType.type_id),
      );
    }
  }, [selectedGcType]);

  useEffect(() => {
    if (selectedGcCardList.length > 0) {
      setSelectedGcCard(selectedGcCardList[0]);
      updateFormData({
        gcTypeId: selectedGcType.type_id,
        gcCardId: selectedGcCardList[0].card_id,
        message: selectedGcCardList[0].message,
        gcAmount: selectedGcCardList[0].amount,
      });
    }
  }, [selectedGcCardList]);

  useEffect(() => {
    stepsVisibility(false);
    setModalSubtitle(subTitle);
  }, []);

  useEffect(() => {
    const { programId = '' } = getCachedFormData();
    if (programId && programId !== '')
      getProgramInfo({
        programId,
        workspaceId: selectedWorkspace?.workspace_id,
      });
  }, [reviewProgramDetails]);

  useEffect(() => {
    const { programId = '' } = getCachedFormData();
    if (
      programId &&
      programId !== '' &&
      !fetchingProgramInfo &&
      fetchedProgramInfo
    ) {
      if (planDataFlag === programStatusEnum.DRAFTED) {
        updateFormData({
          programId: programInfo?.program_id ?? '',
          name: programInfo?.name ?? '',
          email: programInfo?.poc_email ?? '',
          workspaceID: selectedWorkspace?.workspace_id,
          amount: programInfo?.amount,
          startDate: programInfo?.start_date ?? minStartDate,
          showError: false,
        });
      }
    }
  }, [fetchingProgramInfo]);

  const gstListItems = () => {
    return GSTList.map(value => {
      return <MenuItem value={value} label={value.gst_number} />;
    });
  };

  const dateCheck = () => {
    if (Date.parse(`${formData.startDate}`) < Date.parse(`${minStartDate}`)) {
      showErrorToast(programStartDateError);
      return false;
    }
    return true;
  };

  const emailCheck = () => {
    if (!validateEmail(formData.email)) {
      return false;
    }
    return true;
  };

  const getDateFromString = str => {
    const date = str.split(' ')[0];
    return date;
  };

  useEffect(() => {
    const {
      validation_errors: validationErrors = [],
      program_id: programId,
    } = reviewProgramDetails;
    updateFormData({ showError: true });
    updateCachedFormData({ ...formData, programId });

    if (nextPlagFlag && !_isEmpty(reviewProgramDetails)) {
      setNextPlagFlag(false);

      if (validationErrors?.length > 0) {
        updateFormData({ showError: true });
        setErrorValidation(validationErrors);
      } else {
        updateCachedFormData({ ...formData, programId });
        goToNextStep();
      }
    }
  }, [reviewProgramDetails]);

  useEffect(() => {
    if (
      !!formData.name &&
      !!formData.email &&
      !!formData.startDate &&
      !!formData.amount &&
      parseInt(formData.amount, 10) !== 0 &&
      !!formData.message &&
      formData.message?.length <= 100 &&
      !!formData.gcCardId &&
      !!formData.gcTypeId &&
      !!(selectedOffice.office_id || isConnectedToStagging())
    ) {
      setDisabled(false);
    } else if (!disabled) setDisabled(true);
  }, [formData]);

  const handleOfficeChange = val => {
    setSelectedOffice(val);
  };

  const handleStartDateChange = updateForm => newDate => {
    updateForm({
      showError: false,
      startDate: newDate,
    });
  };

  const handleAmountChange = updateForm => newAmount => {
    const val = Number.isInteger(parseInt(newAmount, Number)) ? newAmount : '';
    if (val >= 0) {
      updateForm({
        showError: false,
        amount: val,
      });
    }
  };

  const handleMessageChange = updateForm => newMessage => {
    if (newMessage.length > 100) {
      setMsgError('Message should be less than 100 characters');
    } else {
      setMsgError('');
      updateForm({
        showError: false,
        message: newMessage,
      });
    }
  };
  const handleGcTypeChange = updateForm => val => {
    const selectedGcType2 = gcTypes.find(value => value.type_id === val);
    setSelectedGcType(selectedGcType2);
    updateForm({
      showError: false,
      gcTypeId: val,
    });
  };

  const handleNextBtnClick = () => {
    if (dateCheck() && emailCheck()) {
      // caches the data
      updateCachedFormData(formData);
      setNextPlagFlag(true);

      const { programId } = getCachedFormData();

      const programBody = {
        name: formData.name,
        poc_email: formData.email,
        program_id: programId ?? '',
        group_ids: [],
        start_date: formData.startDate,
        end_date: '',
        never_ends: false,
        workspace_id: selectedWorkspace?.workspace_id ?? '',
        amount: parseFloat(formData.amount) ?? 0,
        restrictions: {},
        topup_type: topupTypeStatusEnum.ACCUMULATING,
        cycle_type: cycleTypeEnum.ONE_TIME,
        restriction_type: programRestrictionsTypes.NO_RESTRICTION,
        flow_type: flowType,
        question_ids: [],
        gift_card_details: {
          type_id: formData.gcTypeId,
          card_id: formData.gcCardId,
          message: formData.message,
          amount: parseFloat(formData.amount) ?? 0,
        },
        office_details: {
          office_id: selectedOffice.office_id ?? 0,
        },
      };

      reviewProgram({ programBody });
    }
    ZTrackingClient.recordClick({
      ui_source: 'SEND_EGIFT_CARD_MODAL',
      component: 'ADD_RECIPIENTS_BUTTON',
    });
  };

  const handleCardChange = val => {
    if (
      val !== undefined &&
      selectedGcCardList.length > 0 &&
      selectedGcCardList[val]?.card_id !== formData.gcCardId
    ) {
      setSelectedGcCard(selectedGcCardList[val]);
      updateFormData({
        gcTypeId: selectedGcCardList[val]?.type_id,
        gcCardId: selectedGcCardList[val]?.card_id,
        message: selectedGcCardList[val]?.message,
        gcAmount: selectedGcCardList[val]?.amount,
      });
    }
  };

  const gcTypeItems = () => {
    return gcTypes.map(value => {
      return <MenuItem value={value.type_id} label={value.type_name} />;
    });
  };

  const carouselComponent = val => {
    return (
      <div style={{ padding: '0 20px' }}>
        <img
          width='100%'
          alt='aww snap'
          src={val.image_url}
          style={{ borderRadius: '10px' }}
        />
      </div>
    );
  };

  return (
    <>
      <Card.Body style={bodyStyle}>
        <StyledLayout gap={20}>
          <Layout.Item colD={6} colT={6} colM={6}>
            <InputWrapper>
              <StyledInput
                innerWidth='100%'
                type='date'
                label='Execution date'
                value={getDateFromString(formData.startDate)}
                min={minStartDate}
                max={maxStartDate}
                onChange={handleStartDateChange(updateFormData)}
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={6} colT={6} colM={6}>
            <InputWrapper>
              <StyledInput
                value={formData.amount}
                innerWidth='100%'
                label='Amount per gift card'
                onChange={handleAmountChange(updateFormData)}
              />
            </InputWrapper>
          </Layout.Item>
          {GSTList?.length > 1 && (
            <>
              <Layout.Item colD={12} colT={12} colM={12}>
                <LabelWrapper>
                  <P>Select office</P>
                </LabelWrapper>
              </Layout.Item>
              <Layout.Item colD={12} colT={12} colM={12}>
                <InputWrapper style={{ marginBottom: '1rem' }}>
                  <Dropdown
                    width='100%'
                    value={
                      selectedOffice.gst_number ?? (
                        <div style={{ color: grey.z500 }}>Select GST</div>
                      )
                    }
                    onSelect={handleOfficeChange}
                  >
                    {gstListItems()}
                  </Dropdown>
                </InputWrapper>
              </Layout.Item>
            </>
          )}
          <UploadFileWrapper>
            <Layout.Item colD={12} colT={12} colM={12}>
              <FundsSummaryText>
                <H5>ADD CUSTOMISATION</H5>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <H6>
                    Zomato will email the claim link with the customisations to
                    your recipients.
                  </H6>
                  <Tooltip
                    position='right'
                    label={() => (
                      <ZomatoGiftCard
                        imageUrl={selectedGcCard.image_url}
                        userMessage={formData.message}
                        formattedAmount={formatAmountInRupees(
                          formData.amount ?? 0,
                        )}
                        companyName={companyProfile?.company_short_name}
                      />
                    )}
                    className='tooltipInfo'
                    size='small'
                    appearance='light'
                    widthMaxContent
                    style={{
                      position: 'absolute',
                      top: '50%',
                      marginLeft: '70px',
                      transform: 'translate(0, -50%)',
                      borderRadius: '16px',
                      width: '320px',
                      height: '823px',
                    }}
                  >
                    <H2>
                      Preview email 
                      {' '}
                      <ChevronRight color={red.z600} />
                      {' '}
                    </H2>
                  </Tooltip>
                </div>
              </FundsSummaryText>
            </Layout.Item>
            <br />
            <Layout.Item colD={12} colT={12} colM={12}>
              <LabelWrapper>
                <P>Occasion</P>
              </LabelWrapper>
            </Layout.Item>
            <Layout.Item colD={12} colT={12} colM={12}>
              <InputWrapper>
                <Dropdown
                  value={selectedGcType.type_name}
                  onSelect={handleGcTypeChange(updateFormData)}
                  width='100%'
                >
                  {gcTypeItems()}
                </Dropdown>
              </InputWrapper>
            </Layout.Item>
            <Layout.Item colD={12} colT={12} colM={12}>
              <InputWrapper style={{ padding: '4rem 15rem' }}>
                <Carousel
                  componentData={selectedGcCardList}
                  componentToRender={carouselComponent}
                  onSlideChange={handleCardChange}
                  infinite={false}
                  isResponsive
                  height='100%'
                  slidesToShow={1}
                  innerMargin={40}
                />
              </InputWrapper>
            </Layout.Item>
            <Layout.Item colD={12} colT={12} colM={12}>
              <LabelWrapper>
                <P>Message (100 characters allowed)</P>
              </LabelWrapper>
            </Layout.Item>
            <Layout.Item colD={12} colT={12} colM={12}>
              <StyledTextArea
                value={formData.message || ''}
                helperText={`Character Limit: ${formData.message?.length}/100`}
                onChange={handleMessageChange(updateFormData)}
                errorMsg={msgError}
              />
            </Layout.Item>
          </UploadFileWrapper>
        </StyledLayout>
        {formData.showError && errorValidation.length > 0 && (
          <>
            <BottomScroll />
            <ErrorContainer>
              {errorValidation.map(err => {
                return <p>{err}</p>;
              })}
            </ErrorContainer>
          </>
        )}
      </Card.Body>
      <Card.Footer style={footerStyle}>
        <Button
          onClick={handleNextBtnClick}
          loading={reviewingProgram}
          disabled={disabled}
          icon={<ChevronRight />}
          iconAlign='right'
        >
          Add recipients
        </Button>
      </Card.Footer>
    </>
  );
};

AdhocProgram.propTypes = {
  subTitle: PropTypes.string,
  companyProfile: PropTypes.objectOf(PropTypes.any),
  userData: PropTypes.objectOf(PropTypes.any),
  flowType: PropTypes.oneOf(currentFlowTypeEnum),
  planData: PropTypes.objectOf(PropTypes.any),
  planDataFlag: PropTypes.oneOf(adhocProgramFormStates),
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  showErrorToast: PropTypes.func,
  reviewProgram: PropTypes.func,
  reviewingProgram: PropTypes.bool,
  reviewProgramDetails: PropTypes.objectOf(PropTypes.any),
  programInfo: PropTypes.objectOf(PropTypes.any),
  getProgramInfo: PropTypes.func,
  fetchingProgramInfo: PropTypes.bool,
  fetchedProgramInfo: PropTypes.bool,
};

AdhocProgram.defaultProps = {
  subTitle: '',
  companyProfile: {},
  userData: {},
  flowType: currentFlowTypeEnum.FLOW_TYPE_GIFT_CARD,
  planData: {},
  planDataFlag: programStatusEnum.DRAFTED,
  selectedWorkspace: {},
  showErrorToast: _noop,
  reviewProgramDetails: {},
  reviewProgram: _noop,
  reviewingProgram: false,
  programInfo: {},
  getProgramInfo: _noop,
  fetchedProgramInfo: true,
  fetchingProgramInfo: true,
};

export default AdhocProgram;
